import {
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import TablePagination from "@quest_editor/components/TablePagination";
import SectionHeader from "@quest_editor/components/SectionHeader";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { Promotion } from "@quest_editor/mocks";
import {
  getPromotionTypeOptions,
  getPromotionStatusOptions,
  getOwnerTypeOptions,
} from "@quest_editor/utils/view/constLabels";

export type PromotionFilters = {
  idName: string;
  type: string;
  status: string;
  owner: string;
};

type TablePanelProps = {
  pageRows: Promotion[];
  filteredTotal: number;
  filters: PromotionFilters;
  onFilterChange: (key: keyof PromotionFilters, value: string) => void;
  onSearch: () => void;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
};

const TablePanel = ({
  pageRows,
  filteredTotal,
  filters,
  onFilterChange,
  onSearch,
  page,
  pageCount,
  onPageChange,
}: TablePanelProps) => {
  const { t } = useTranslation("questEditor");

  const promotionTypes = getPromotionTypeOptions();
  const promotionStatuses = getPromotionStatusOptions();
  const ownerTypes = getOwnerTypeOptions();

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        height: "100%",
      }}
    >
      <SectionHeader title={t("home.table.title")} />
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={2}
        px={1}
        py={2}
      >
        <Stack direction="row" spacing={2}>
          <FormControl>
            <TextField
              variant="outlined"
              size="small"
              label={t("home.table.filter.id_name.label")}
              value={filters.idName}
              onChange={(e) => onFilterChange("idName", e.target.value)}
            ></TextField>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="type-filter-label">
              {t("home.table.filter.type.label")}
            </InputLabel>
            <Select
              labelId="type-filter-label"
              label={t("home.table.filter.type.label")}
              value={filters.type}
              onChange={(e) => onFilterChange("type", e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">{t("home.table.filter.type.all")}</MenuItem>
              {promotionTypes.map((pt) => (
                <MenuItem key={pt.value} value={pt.value}>
                  {pt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="status-filter-label">
              {t("home.table.filter.status.label")}
            </InputLabel>
            <Select
              labelId="status-filter-label"
              label={t("home.table.filter.status.label")}
              value={filters.status}
              onChange={(e) => onFilterChange("status", e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">{t("home.table.filter.status.all")}</MenuItem>
              {promotionStatuses.map((ps) => (
                <MenuItem key={ps.value} value={ps.value}>
                  {ps.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="owner-filter-label">
              {t("home.table.filter.owner.label")}
            </InputLabel>
            <Select
              labelId="owner-filter-label"
              label={t("home.table.filter.owner.label")}
              value={filters.owner}
              onChange={(e) => onFilterChange("owner", e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">{t("home.table.filter.owner.all")}</MenuItem>
              {ownerTypes.map((ot) => (
                <MenuItem key={ot.value} value={ot.value}>
                  {ot.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <SubActionButton onClick={onSearch}>
          {t("home.table.button.search")}
        </SubActionButton>
      </Stack>

      <Divider />

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t("home.table.header.id")}</TableCell>
              <TableCell>{t("home.table.header.name")}</TableCell>
              <TableCell>{t("home.table.header.type")}</TableCell>
              <TableCell>{t("home.table.header.status")}</TableCell>
              <TableCell>{t("home.table.header.progress")}</TableCell>
              <TableCell>{t("home.table.header.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" py={4}>
                    {t("common.label.no_data")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span style={{ display: "inline-block", minWidth: 60 }}>
                        {promotionTypes.find((pt) => pt.value === row.type)?.label}
                      </span>
                      {row.options.template && (
                        <Chip label="Template" size="small" color="warning" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <PromotionStatusChip status={row.status} />
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignSelf="flex-end">
                      <SubActionButton
                        component={Link}
                        to={`promotion/${row.id}`}
                      >
                        {t("home.table.button.detail")}
                      </SubActionButton>
                      <SubActionButton
                        component={Link}
                        to={`promotion/${row.id}/duplication`}
                      >
                        {t("home.table.button.duplication")}
                      </SubActionButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />

      <TablePagination
        total={filteredTotal}
        page={page}
        setPage={onPageChange}
        count={pageCount}
      />
    </Paper>
  );
};
export default TablePanel;
